// 게임 이벤트 → 소리 결정(순수 로직). WebAudio를 모른다. 미리 할당한 CueList에 써서 프레임 루프 할당을 피한다.
import { Ev, HitFlag, TeleSource, type GameEvent } from '../shared/events';
import { KAEL_SKILLS_BY_JOB } from '../shared/skills/kael';
import type { SkillHeroId } from '../shared/skills/types';
import { BurstKind } from '../shared/growth';
import type { HazardTheme } from '../shared/levels';
import { BossKind } from '../shared/types';
import { Group, S, SFX_BY_ID } from './sounds';
import { isWyvernEvent, raidOwnsJingle, roleOf } from './raid/decide';

/** Ev.Hit flags(events.ts 주석이 정본) */
export const HitBit = { Crit: 1, Finisher: 2, Skill: 4, Awaken: 8, MultiHit: 16, Blocked: 32 } as const;

/** 재생 방식: 0 그대로, 1 상승 스윕(각성 모으기: 재생 속도 0.55→1.5) */
export const Sweep = { None: 0, Rise: 1 } as const;

export interface Cue {
  id: number;
  gainDb: number;
  /** 재생 속도(피치) 배율. 무작위 흔들림은 엔진이 sounds.jitter에 따라 더한다 */
  rate: number;
  /** 피치 흔들림 폭 배율(1 = ±6%). 콤보 단계 피치 상승이 들리도록 휘두름은 줄인다 */
  jitter: number;
  /** 동시 시작 제한 묶음 덮어쓰기(-1 = 소리 정의의 묶음). 겹쳐 얹는 저역 층이 타격 묶음 자리를 뺏지 않게 한다 */
  group: number;
  x: number;
  z: number;
  positional: boolean;
  sweep: number;
}

export class CueList {
  readonly items: Cue[];
  count = 0;
  constructor(capacity = 6) {
    this.items = [];
    for (let i = 0; i < capacity; i++) this.items.push({ id: 0, gainDb: 0, rate: 1, jitter: 1, group: -1, x: 0, z: 0, positional: false, sweep: 0 });
  }
  clear(): void {
    this.count = 0;
  }
  add(id: number, gainDb = 0, rate = 1, x = 0, z = 0): Cue | null {
    if (this.count >= this.items.length) return null;
    const c = this.items[this.count++];
    c.id = id; c.gainDb = gainDb; c.rate = rate; c.x = x; c.z = z;
    c.jitter = 1; c.group = -1;
    c.positional = SFX_BY_ID[id].positional;
    c.sweep = Sweep.None;
    return c;
  }
}

export interface DecideState {
  /** 0..1 난수 */
  rand(): number;
  /** 직전 일반 타격음(같은 소리 연속을 피한다) */
  lastHit: number;
  /** 귀환서 소비(Ev.Consume a 3)를 막 처리했다: 바로 뒤에 같은 명령의 GearCmd(b 13)가 오므로 구매음과 구별하는 표식 */
  scrollUsed?: boolean;
}

const HITS = [S.hit0, S.hit1, S.hit2];

/** 기본 공격 휘두름 단계(0..2)별 피치: 단계가 오를수록 조금씩 높게(0, +3%, +6%). 4타(마무리)는 낮고 무겁게 둔다 */
export const SWING_STEP_RATE: readonly number[] = [1, 1.03, 1.06];
/** 단계 피치 차이가 흔들림에 묻히지 않도록 휘두름의 무작위 폭은 ±2%로 줄인다 */
export const SWING_JITTER_SCALE = 0.35;
/** 마무리타에 한 겹 더하는 저역 임팩트: 마무리 타격음을 낮은 피치로 깔고, 자기 묶음(Layer)으로 세어 다른 소리 자리를 뺏지 않는다 */
export const FINISH_LAYER = { gainDb: -8, rate: 0.78 } as const;

/** 직업 AI(레이드 용병) 타격·피격 음량(dB): 영웅 소리가 앞에 서게 */
export const ALLY_HIT_DB = -4;
export const ALLY_HURT_DB = -6;

/** 화면(UI) 소리: 월드 위치가 없으니 거리 감쇠·좌우 팬을 쓰지 않는다 */
function uiCue(out: CueList, id: number): void {
  const c = out.add(id);
  if (c) c.positional = false;
}

/**
 * 이벤트 하나를 소리 0~2개로 바꾼다. 몹 공격(상태 변화)·BGM·덕킹은 엔진이 따로 처리한다.
 * theme = 지금 맵 위험 테마: 서리·독 분출구 경고·분출은 절차 효과음(synth/decide.ts)이 맡아 여기서는 소리를 내지 않는다(c019).
 * hero = 지금 영웅(c022). 라이라는 활·화살 소리를 절차 효과음(synth/lyraSfx.ts lyraCueOf)이 내므로 카엘 검 소리(스킬 시전·휘두름·각성 참격)와
 * 화살 일반 타격음을 여기서 내지 않는다. 카엘(기본값)은 예전 그대로.
 * raid = 레이드 판(view.raid.active). 비룡 몸·패턴·판정 소리와 레이드 클리어·전멸 징글은 레이드 절차음(raid/)이 맡아 여기서는 내지 않는다.
 */
export function decideEvent(e: GameEvent, out: CueList, st: DecideState, bossKind: number = BossKind.Treant, theme: HazardTheme = 'ember', hero: SkillHeroId = 'kael', raid = false): void {
  if (isWyvernEvent(e, bossKind) || raidOwnsJingle(e, raid)) return;
  const lyra = hero === 'lyra';
  switch (e.type) {
    case Ev.Slash: {
      if (lyra) break;
      const b = e.b | 0;
      if (b >= 0 && b <= 3) {
        const finisher = b === 3 || (e.flags & 1) !== 0;
        const c = out.add(finisher ? S.swing3 : S.swing0 + b, 0, finisher ? 1 : SWING_STEP_RATE[b], e.x, e.z);
        if (c) c.jitter = SWING_JITTER_SCALE;
        if (finisher) {
          const low = out.add(S.hitFinisher, FINISH_LAYER.gainDb, FINISH_LAYER.rate, e.x, e.z);
          if (low) { low.group = Group.Layer; low.jitter = SWING_JITTER_SCALE; }
        }
      }
      else if (b === 13) out.add(S.skill3Land, 0, 1, e.x, e.z); // 붉은 낙화 착지
      else if (b === 11) { /* 피의 돌진: SkillCast 소리가 휘두름을 겸한다 */ }
      else if (b === 14) out.add(S.skill4Wave, 0, 1, e.x, e.z); // 진홍 초승달 검기 발사
      // C9 새 스킬: 소리는 아래 SkillCue sfx 큐(공중 난무 2번·내려찍기·시전·마무리)가 내고, 판정 순간 Slash는 여기서
      else if (b === 16 || b === 17) { /* 공중 난무 5타·착지 / 대지 가르기 내려찍기: 큐 air_slash·skill3_land·split_impact가 낸다 */ }
      else if (b === 18) { // 혈검 회전베기 9회전: 회차마다 낮은 휘두름 + 피치 변주(같은 소리가 반복돼 지루하지 않게)
        const c = out.add(S.swingSkill, -5, 0.92 + st.rand() * 0.22, e.x, e.z);
        if (c) c.jitter = SWING_JITTER_SCALE;
      }
      else if (b >= 10) out.add(S.swingSkill, 0, 1, e.x, e.z);
      break;
    }
    case Ev.Hit: {
      const f = e.flags | 0;
      if (e.b === 2) break; // 영웅 피격은 HeroHurt에서 한 번만 낸다
      if ((f & HitFlag.Ally) !== 0) {
        // 레이드 직업 AI: 보스를 친 타격(b 1, c 칸 1..3)은 방패병만 둔탁한 보스 타격음(작게), 궁수·사제는 레이드 절차음이 낸다.
        // 직업 AI가 맞은 피해(b 3)는 영웅 타격보다 한 층 작게
        if (e.b === 1) { if (roleOf((e.c | 0) - 1) === 'tank') out.add(S.hitBoss, ALLY_HIT_DB, 1, e.x, e.z); }
        else out.add(HITS[(e.c | 0) % 3], ALLY_HURT_DB, 1, e.x, e.z);
        break;
      }
      if ((f & HitBit.Blocked) !== 0) { out.add(S.blocked, 0, 1, e.x, e.z); break; }
      if (e.b === 1) {
        out.add(S.hitBoss, 0, 1, e.x, e.z);
        if (lyra && (f & HitFlag.Arrow) !== 0) break; // 화살 보스 타격: 둔탁한 보스음 + 절차 arrow_hit만
        if ((f & (HitBit.Crit | HitBit.Finisher)) !== 0) out.add((f & HitBit.Finisher) !== 0 ? S.hitFinisher : S.hitCrit, -4, 1, e.x, e.z);
        break;
      }
      if ((f & (HitBit.MultiHit | HitBit.Awaken)) !== 0) out.add(S.hitLight, (f & HitBit.Crit) !== 0 ? 3 : 0, 1, e.x, e.z);
      else if ((f & HitBit.Finisher) !== 0) out.add(S.hitFinisher, 0, 1, e.x, e.z);
      else if ((f & HitBit.Crit) !== 0) out.add(S.hitCrit, 0, 1, e.x, e.z);
      else if (lyra && (f & HitFlag.Arrow) !== 0) { /* 화살 일반 타격: 절차 arrow_hit·arrow_pierce가 낸다 */ }
      else if ((f & HitBit.Skill) !== 0) out.add(S.hitSkill, 0, 1, e.x, e.z);
      else {
        let k = Math.floor(st.rand() * 3);
        if (k > 2) k = 2;
        if (HITS[k] === st.lastHit) k = (k + 1) % 3;
        st.lastHit = HITS[k];
        out.add(HITS[k], 0, 1, e.x, e.z);
      }
      break;
    }
    case Ev.Kill:
      out.add((e.a | 0) === 2 ? S.killBig : S.kill, 0, 1, e.x, e.z);
      break;
    case Ev.MassKill:
      out.add(S.massKill);
      break;
    case Ev.ComboPraise: {
      const t = e.a | 0;
      if (t >= 1 && t <= 4) out.add(S.praise1 + t - 1);
      break;
    }
    case Ev.AwakenPhase:
      if (e.a === 1) { const c = out.add(S.awakenGather); if (c) c.sweep = Sweep.Rise; }
      else if (e.a === 2) out.add(S.awakenFlash);
      break;
    case Ev.AwakenSlash: {
      if (lyra) break; // 폭풍 화살비 한 파: 절차 arrow_rain
      const n = e.a | 0;
      // 참격마다 조금씩 높게, 마지막(6번째)은 크게
      out.add(S.awakenSlash, n >= 5 ? 3 : 0, 1 + 0.04 * n);
      if (n >= 5) out.add(S.hitFinisher, -3);
      break;
    }
    case Ev.SkillCast: {
      if (lyra) break; // 라이라 시전음은 스킬 큐(bow_draw 등, 절차 효과음)
      const id = e.a | 0;
      if (id >= 1 && id <= 3) out.add(S.skill1 + id - 1, 0, 1, e.x, e.z);
      else if (id === 4) out.add(S.skill4Cast, 0, 1, e.x, e.z);
      else if (id === 5) out.add(S.skill4Cast, 0, 0.88, e.x, e.z); // 천공 올려베기 시전: 새 파일 없이 시전음을 낮춰 묵직하게(타격은 Slash b=15가 낸다)
      // C9: 공중 난무 = 낙화 도약음을 높게 / 대지 가르기 = 초승달 시전음을 더 낮게 / 혈검 회전베기 = 칼날 폭풍 시전음(슈퍼아머 시작)을 조금 낮게
      else if (id === 6) out.add(S.skill3, 0, 1.12, e.x, e.z);
      else if (id === 7) out.add(S.skill4Cast, 0, 0.78, e.x, e.z);
      else if (id === 8) out.add(S.skill2, 0, 0.9, e.x, e.z);
      // C10-5 전직 스킬(갈래 무관 시전음, 큐 소리는 아래 SkillCue): 9 = 칼날 폭풍 시전음을 낮고 무겁게 / 10 = 초승달 시전음을 낮게
      else if (id === 9) out.add(S.skill2, 1, 0.8, e.x, e.z);
      else if (id === 10) out.add(S.skill4Cast, 1, 0.74, e.x, e.z);
      break;
    }
    case Ev.SkillCue: {
      // 카엘 스킬 타임라인의 sfx 채널(C9 새 스킬만 여기서 낸다. 1~5는 SkillCast·Slash가 이미 낸다). 라이라 큐는 절차 효과음(lyraSfx)
      if (lyra || e.b !== 1) break;
      const sid = e.a | 0;
      if (sid < 6 || sid > 10) break;
      // 9·10(C10-5 전직)은 갈래 표에서 찾는다(flags & 2 = 소드 마스터)
      const name = KAEL_SKILLS_BY_JOB[(e.flags & 2) !== 0 ? 1 : 0][sid - 1]?.cues[e.c | 0]?.name;
      if (name === 'air_slash') {
        // 8틱(첫 타)·32틱(다섯째 타): 기존 휘두름 샘플을 높게(1.2 / 1.32)
        const c = out.add(S.swingSkill, -1, (e.c | 0) === 5 ? 1.2 : 1.32, e.x, e.z);
        if (c) c.jitter = SWING_JITTER_SCALE;
      } else if (name === 'skill3_land') {
        out.add(S.skill3Land, -2, 1.06, e.x, e.z); // 공중 난무 착지 마무리(낙화 착지음을 한 단계 작고 높게)
      } else if (name === 'split_impact') {
        out.add(S.skill3Land, 1, 0.72, e.x, e.z); // 대지 가르기 내려찍기: 낙화 착지 샘플을 낮고 크게 + 마무리 타격 저역 층
        const low = out.add(S.hitFinisher, -3, 0.8, e.x, e.z);
        if (low) low.group = Group.Layer;
      } else if (name === 'spin_start') {
        out.add(S.skill2, 1, 0.82, e.x, e.z); // 혈검 회전베기 시전: 폭풍 시전음을 낮게(위 SkillCast와 겹쳐 두터움)
      } else if (name === 'skill4_wave') {
        out.add(S.skill4Cast, -1, 1.08, e.x, e.z); // 검기 방출(C10-5): 초승달 발사음을 조금 높고 작게 세 번
      } else if (name === 'skill_swing' && sid >= 9) {
        const c = out.add(S.swingSkill, -1, 0.92, e.x, e.z); // 천검 낙하·흡혈 참격 시작 휘두름
        if (c) c.jitter = SWING_JITTER_SCALE;
      } else if (name === 'spin_finish') {
        out.add(S.skill3Land, 0, 0.9, e.x, e.z);
        const low = out.add(S.hitFinisher, -2, 0.85, e.x, e.z);
        if (low) low.group = Group.Layer;
      }
      break;
    }
    case Ev.Dodge:
      out.add(S.dodge, 0, 1, e.x, e.z);
      break;
    case Ev.LootDrop: {
      // 2026-11-03 등급별 드롭(SeedAudio): a 0 장비면 b 등급(0 일반 … 4 전설), 이름 있는 유니크(flags&1)는 전설 소리. 장비가 아니면 일반 소리
      const g = (e.a | 0) === 0 ? ((e.flags & 1) !== 0 ? 4 : Math.max(0, Math.min(4, e.b | 0))) : 0;
      out.add(S.drop0 + g, 0, 1, e.x, e.z);
      break;
    }
    case Ev.Consume:
      // a 0 HP 물약, 1 MP 물약, 2 비약 → 마시는 소리 / 3 귀환서, 4 부활 깃털 → 마법 아이템
      out.add((e.a | 0) <= 2 ? S.potionDrink : S.magicItem);
      if (st) st.scrollUsed = (e.a | 0) === 3;
      break;
    case Ev.ConsumeDenied:
    case Ev.StageLocked:
      out.add(S.uiDeny);
      break;
    case Ev.GearCmd: {
      // 장비 창 버튼은 클릭음을 끄고(data-sfx=event) 결과로 소리를 정한다. 강화(3)는 대장간 절차 효과음(GearEnhance)이 낸다
      // 소비 성공(비약 b 9, 귀환서 b 13)은 Ev.Consume이 이미 소리를 냈으니 무음(실패는 거절음 유지).
      // 비약은 성공 시 Consume이 반드시 먼저 나온다(game.ts useElixirOne). b 13은 상점 구매(gearBuy)와 번호를 같이 쓰므로,
      // 귀환서는 바로 앞에 Consume a 3이 있었는지(scrollUsed)로 구별하고 구매는 그대로 구매음
      const kind = e.b | 0;
      const afterScroll = st?.scrollUsed === true;
      if (st) st.scrollUsed = false;
      if ((e.a | 0) === 0) out.add(S.uiDeny);
      else if (kind === 9) { /* 비약 성공: 마시는 소리가 이미 났다 */ }
      else if (kind === 13 && afterScroll) { /* 귀환서 성공: 마법 아이템 소리가 이미 났다 */ }
      else if (kind === 7 || kind === 13 || kind === 5) out.add(S.buyCoins);
      else if (kind !== 3) out.add(S.equipGear);
      break;
    }
    case Ev.HeroSelect:
      out.add((e.a | 0) === 1 ? S.heroSelect : S.uiDeny);
      break;
    case Ev.Ko:
      // 결정타 KO(느려지는 순간): 보스는 크게, 엘리트·방 마지막 몹은 조금 작게
      out.add(S.koImpact, (e.a | 0) === 2 ? 0 : -5, 1, e.x, e.z);
      break;
    case Ev.LootPickup:
      // 영웅이 직접 주운 장비: 장비 부스럭 + (절차 효과음의 등급 줍기 벨이 위에 얹힌다)
      if ((e.a | 0) === 0 && (e.c | 0) === 0) out.add(S.pickupItem);
      break;
    case Ev.GuardBreak:
      // 막힘(금속)과 마무리타(묵직함)를 겹쳐 둘 어느 것과도 다른 '방패가 깨지는' 소리로 만든다(REL-020: 막힘·마무리·브레이크 구별)
      out.add(S.blocked, 0, 0.72, e.x, e.z);
      out.add(S.hitFinisher, -2, 0.9, e.x, e.z);
      break;
    case Ev.JustDodge:
      out.add(S.dodge, 0, 1, e.x, e.z);
      out.add(S.hitCrit, -4, 1, e.x, e.z);
      break;
    case Ev.HeroHurt:
      out.add(S.heroHurt, 0, 1, e.x, e.z);
      break;
    case Ev.WaveStart:
      out.add(S.waveStart, e.a > 0 ? -3 : 0);
      break;
    case Ev.RoomClear:
      out.add(S.roomClear);
      break;
    case Ev.HeroHeal:
      break;
    case Ev.Gate:
      out.add(e.b === 1 ? S.gateOpen : S.gateClose);
      break;
    case Ev.BossIntro:
      out.add(bossKind === BossKind.Ember ? S.emberIntro : S.bossIntro, 0, 1, e.x, e.z);
      break;
    case Ev.BossDefeated:
      out.add(bossKind === BossKind.Ember ? S.emberDefeated : S.bossDefeated, 0, 1, e.x, e.z);
      break;
    case Ev.StageClear:
      out.add(S.stageClear);
      break;
    case Ev.Defeat:
      out.add(S.defeat);
      break;
    case Ev.MobSpawn:
      out.add(S.mobSpawn, 0, 1, e.x, e.z);
      break;
    case Ev.HazardWarn:
      if (theme === 'ember') out.add(S.ventWarn, 0, 1, e.x, e.z);
      break;
    case Ev.HazardErupt:
      if (theme === 'ember') out.add(S.ventErupt, 0, 1, e.x, e.z);
      break;
    case Ev.LavaBurn:
      out.add(S.lavaBurn, 0, 1, e.x, e.z);
      break;
    case Ev.ShamanCast:
      out.add(S.shamanCast, 0, 1, e.x, e.z);
      break;
    case Ev.BossPattern:
      if (e.b === BossKind.Ember) {
        // D3a 추가 패턴(돌진 4·지진 5·세 갈래 물기 6·십자 7·포자 8)은 절차 경고음(synth/decide.ts TELE_BY_PATTERN)이 맡는다(c019)
        const id = e.a === 0 ? S.emberNova : e.a === 1 ? S.emberFissure : e.a === 2 ? S.emberMeteor : e.a === 3 ? S.emberSummon : -1;
        if (id >= 0) out.add(id, 0, 1, e.x, e.z);
      }
      // S5 분노(10)·광폭(11) 진입: 등장 포효를 다시 쓴다(분노는 조금 높고 빠르게, 광폭은 낮고 느리게)
      if (e.a === 10 || e.a === 11) out.add(e.b === BossKind.Ember ? S.emberIntro : S.bossIntro, e.a === 11 ? 1 : 0, e.a === 10 ? 1.12 : 0.88, e.x, e.z);
      break;
    case Ev.HellBrand:
      // 헬 「지옥불 각인」(C10): 새김 = 낮은 불 점화·룬 울림(주술사 시전음을 낮게), 폭발 = 불 폭발(분출구 터짐 재사용). 새 소리 파일 없음
      if (e.a === 0) out.add(S.shamanCast, 0, e.d ? 0.72 : 0.8, e.x, e.z);
      else out.add(S.ventErupt, 2, 0.85, e.x, e.z);
      break;
    case Ev.TelegraphFire:
      if (e.c === TeleSource.Vent || e.c === TeleSource.HellBrand) break; // 같은 틱 HazardErupt / Ev.HellBrand 폭발이 유일한 발화음
      if (e.c === TeleSource.Shaman || (e.c === TeleSource.Boss && e.d === BossKind.Ember)) out.add(S.shamanMortar, 0, 1, e.x, e.z);
      else out.add(S.teleFire, 0, 1, e.x, e.z);
      break;
    case Ev.LinkFinish:
      // 스킬 연계 3단 완성: 짧고 큰 임팩트 한 번(화면 소리라 위치 감쇠 없음)
      out.add(S.linkFinish);
      break;
    case Ev.SkillBurst: {
      // 랭크5·캡스톤 추가 효과(SKILL-TREE 5절). 쌍월은 두 번째 검기 소리를 조금 높고 작게 다시 쓴다
      const k = e.a | 0;
      const id = k === BurstKind.DashWake ? S.burstWake : k === BurstKind.StormEye ? S.burstStormEye : k === BurstKind.Aftershock ? S.burstAftershock
        : k === BurstKind.Bloom ? S.burstBloom : k === BurstKind.Thunder ? S.burstThunder : k === BurstKind.TwinMoon ? S.skill4Wave : -1;
      if (id >= 0) out.add(id, k === BurstKind.TwinMoon ? -3 : 0, k === BurstKind.TwinMoon ? 1.08 : 1, e.x, e.z);
      break;
    }
    case Ev.LevelUp:
      uiCue(out, S.levelUp);
      break;
    case Ev.TrialResult:
      // 용사의 시련: a 2 = 처음 이룸(레벨업 팡파르). 실패·조건 미달·이미 이룬 시련은 StageClear·Defeat 소리만 난다
      if (e.a === 2) uiCue(out, S.levelUp);
      break;
    case Ev.JobChange:
      // 전직(C10-5): 성공(c 1)만 레벨업 팡파르. 거절은 소리 없음(UI 문구가 사유를 말한다)
      if (e.c === 1) uiCue(out, S.levelUp);
      break;
    case Ev.TrialChest:
      // 시련 상자 열기: 카드 고르기보다 한 단계 큰 보상 소리(레벨업)
      uiCue(out, S.levelUp);
      break;
    case Ev.SkillLearned:
      // 레벨업으로 새 스킬 자동 습득(D1e): 트리 배우기와 같은 소리
      uiCue(out, S.skillLearn);
      break;
    case Ev.SkillLearn:
      // a 1 성공(b -1 = 초기화), 0 실패(막힘)
      if (e.a === 1) uiCue(out, e.b < 0 ? S.skillReset : S.skillLearn);
      else uiCue(out, S.blocked);
      break;
    case Ev.Menu:
      // 창 열림(a 1)만 소리를 낸다. 닫기는 버튼 클릭음이 맡는다
      if (e.a === 1) uiCue(out, S.uiClick);
      break;
    case Ev.Trade:
      // a 1 성공, b TxKind(1 buy 2 equip 3 focus 4 recover). 실패는 막힘 소리
      if (e.a === 1) uiCue(out, e.b === 1 ? S.cardPick : S.uiConfirm);
      else uiCue(out, S.blocked);
      break;
    default:
      break;
  }
}

/** 영웅 기준 좌우 거리(m) → 스테레오 팬(-0.7..0.7). 카메라가 돌지 않으므로 월드 x = 화면 좌우. */
export function panFor(dx: number): number {
  const p = dx / 14;
  return p < -0.7 ? -0.7 : p > 0.7 ? 0.7 : p;
}

/** 영웅과의 거리(m) → 감쇠(dB). 5m 안은 그대로, 그 밖은 1m에 0.6dB, 최대 -12dB. */
export function distanceDb(dist: number): number {
  const a = (dist - 5) * 0.6;
  return a <= 0 ? 0 : a >= 12 ? -12 : -a;
}

/** 피치 ±6% 무작위(AV-SPEC). r은 0..1 난수 */
export function jitterRate(r: number): number {
  return 1 + (r * 2 - 1) * 0.06;
}
